/**
 * <kinstone-cinematic-hero> — scroll-driven image-sequence hero for Horizon.
 *
 * Reads everything from data attributes written by sections/kinstone-cinematic-hero.liquid,
 * so a merchant can change every value in the theme editor without touching this file.
 *
 * Frame URLs are derived from the URL of frame 1. Example:
 *   https://cdn.shopify.com/s/files/1/0000/0000/files/frame-001.jpg?v=123
 * becomes frame-001 … frame-180 with the padding, extension and version query preserved.
 */

const PREFERS_REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

class KinstoneCinematicHero extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('[data-kch-canvas]');
    this.loader = this.querySelector('[data-kch-loader]');
    this.pctEl = this.querySelector('[data-kch-pct]');
    this.barEl = this.querySelector('[data-kch-bar]');
    this.stage = this.querySelector('[data-kch-stage]');
    this.hero = this.querySelector('[data-kch-hero]');
    this.brand = this.querySelector('[data-kch-brand]');
    this.lockup = this.querySelector('[data-kch-lockup]');
    this.captions = Array.from(this.querySelectorAll('[data-kch-caption]'));
    this.btnWrap = this.querySelector('[data-kch-btn-wrap]');
    this.topBtn = this.querySelector('[data-kch-top]');
    this.frameNumEl = this.querySelector('[data-kch-frame-num]');
    this.header = this.querySelector('.kch__header');
    if (this.header && this.header.parentNode !== document.body) {
      document.body.appendChild(this.header);
    }

    // "Returning" is either this browsing session or across visits, per the merchant's
    // choice. Storage can throw in private modes, so every access is guarded.
    this.resumeScope = this.dataset.resumeScope === 'session' ? 'session' : 'visits';
    const store = this.resumeScope === 'session' ? window.sessionStorage : window.localStorage;
    let isReturning = false;
    try {
      isReturning = store.getItem('kinstone_hero_seen') === 'true';
      store.setItem('kinstone_hero_seen', 'true');
    } catch (e) {}
    this.isReturning = isReturning;

    if (isReturning && this.loader) {
      this.loader.style.display = 'none';
      this.loader.style.opacity = '0';
    }

    this.total = parseInt(this.dataset.frameCount || '180', 10);
    this.zoom = parseFloat(this.dataset.zoom || '1.35');
    this.zoomTravel = parseFloat(this.dataset.zoomTravel || '0');
    this.parallax = parseFloat(this.dataset.parallax || '24');
    this.fadeStart = parseFloat(this.dataset.fadeStart || '0') / 100;
    this.fadeEnd = parseFloat(this.dataset.fadeEnd || '18') / 100;
    this.brandIn = parseFloat(this.dataset.brandIn || '3.5') / 100;
    this.brandRise = parseFloat(this.dataset.brandRise || '5') / 100;
    this.brandOut = parseFloat(this.dataset.brandOut || '6') / 100;
    this.mobileBreakpoint = 750;

    // Returning visitors can pick the sequence up part-way instead of replaying the
    // opening every time. The frame is 1-based in the theme editor; internally 0-based.
    const wantsResume = this.dataset.resumeEnabled !== 'false';
    const resumeFrame = parseInt(this.dataset.resumeFrame || '1', 10);
    this.resumeIndex = 0;
    if (isReturning && wantsResume && resumeFrame > 1) {
      this.resumeIndex = Math.max(0, Math.min(this.total - 1, resumeFrame - 1));
    }

    this.frames = new Array(this.total);
    this.loadedCount = 0;
    this.index = this.resumeIndex;
    this.ticking = false;

    this.mobileOnlyStatic =
      this.dataset.disableOnMobile === 'true' && window.innerWidth < this.mobileBreakpoint;

    if (this.mobileOnlyStatic || PREFERS_REDUCED.matches) {
      this.finishLoading(true);
      return;
    }

    this.sizeCanvas();
    this.bind();
    this.resumeToFrame();
    this.preload();

    if (isReturning) {
      this.finishLoading(true);
      this.syncOverlays(this.fractionForIndex(this.resumeIndex));
    }
  }

  /**
   * Frame index -> scroll fraction. draw() rounds fraction * (total - 1), so this is
   * its exact inverse and lands the sequence on the requested frame.
   */
  fractionForIndex(index) {
    if (this.total <= 1) return 0;
    return Math.max(0, Math.min(1, index / (this.total - 1)));
  }

  /**
   * Jump a returning visitor to the scroll offset that renders the resume frame.
   *
   * Deliberately skipped when the browser has already restored a scroll position
   * (back button, refresh mid-page) or when an anchor sent the visitor somewhere
   * specific -- overriding either would be hostile. Also skipped in the theme editor,
   * where an unexpected jump makes the section hard to work on.
   */
  resumeToFrame() {
    if (!this.resumeIndex || !this.stage) return;
    if (window.Shopify && window.Shopify.designMode) return;
    if (window.location.hash) return;
    if (window.scrollY > 4) return;

    if ('scrollRestoration' in history) {
      try { history.scrollRestoration = 'manual'; } catch (e) {}
    }

    const jump = () => {
      const span = this.stage.offsetHeight - window.innerHeight;
      if (span <= 0) return;
      const stageTop = this.stage.getBoundingClientRect().top + window.scrollY;
      const target = Math.round(stageTop + this.fractionForIndex(this.resumeIndex) * span);
      window.scrollTo(0, target);
      this.draw();
    };

    // One frame for layout to settle, then again after load in case late assets
    // (fonts, the header) shifted the stage.
    requestAnimationFrame(jump);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', jump, { once: true });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mousemove', this.onMouse);
  }

  /* ---------- frame urls ---------- */

  frameUrl(i) {
    const first = this.dataset.firstFrameUrl || '';
    const match = first.match(/^(.*?)(\d+)(\.[a-zA-Z0-9]+)(\?.*)?$/);
    if (!match) return first;
    const [, prefix, digits, ext, query = ''] = match;
    const start = parseInt(digits, 10);
    const n = String(start + i).padStart(digits.length, '0');
    return `${prefix}${n}${ext}${query}`;
  }

  /* ---------- preloading ---------- */

  preload() {
    const concurrency = 8;

    // Fetch whichever frame will actually be on screen first. Without this, a visitor
    // resuming mid-sequence looks at frame 1 (the fallback in drawFrame) until their
    // frame happens to come down in sequential order.
    const firstNeeded = this.resumeIndex || 0;
    const order = [firstNeeded];
    for (let i = 0; i < this.total; i++) {
      if (i !== firstNeeded) order.push(i);
    }

    let next = 0;

    const loadOne = (i) =>
      new Promise((resolve) => {
        const img = new Image();
        img.decoding = 'async';
        img.onload = img.onerror = () => {
          if (img.naturalWidth) this.frames[i] = img;
          this.loadedCount++;
          this.reportProgress();
          resolve();
        };
        img.src = this.frameUrl(i);
      });

    const worker = async () => {
      while (next < order.length) {
        const i = order[next++];
        await loadOne(i);
        // Paint as soon as the on-screen frame is available so the hero is never empty.
        if (i === firstNeeded) this.draw();
      }
    };

    Promise.all(Array.from({ length: concurrency }, worker)).then(() => this.finishLoading());
  }

  reportProgress() {
    const pct = Math.round((this.loadedCount / this.total) * 100);
    if (this.pctEl) this.pctEl.textContent = `${pct}%`;
    if (this.barEl) this.barEl.style.transform = `scaleX(${pct / 100})`;
  }

  finishLoading(skipped) {
    this.setAttribute('data-ready', 'true');
    if (this.loader) {
      this.loader.style.opacity = '0';
      setTimeout(() => this.loader && (this.loader.style.display = 'none'), 700);
    }
    if (!skipped) this.draw();
  }

  /* ---------- canvas ---------- */

  sizeCanvas() {
    if (!this.canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = window.innerWidth * dpr;
    this.canvas.height = window.innerHeight * dpr;
  }

  scrollFraction() {
    if (!this.stage) return 0;
    const rect = this.stage.getBoundingClientRect();
    const span = this.stage.offsetHeight - window.innerHeight;
    return Math.max(0, Math.min(1, -rect.top / Math.max(1, span)));
  }

  requestDraw() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.draw();
    });
  }

  draw() {
    const f = this.scrollFraction();
    const index = Math.min(this.total - 1, Math.round(f * (this.total - 1)));
    this.index = index;
    this.drawFrame(index);
    this.syncOverlays(f);
  }

  /**
   * Manual object-fit: cover with a ZOOM_FACTOR, so baked-in letterboxing
   * on the source frames is cropped away at any viewport ratio.
   */
  drawFrame(index) {
    const img = this.frames[index] || this.frames.find(Boolean);
    if (!this.canvas || !img) return;
    const ctx = this.canvas.getContext('2d');
    const cw = this.canvas.width;
    const ch = this.canvas.height;
    const t = this.total > 1 ? index / (this.total - 1) : 0;
    const zoom = this.zoom * (1 + t * this.zoomTravel);
    const cover = Math.max(cw / img.width, ch / img.height) * zoom;
    const dw = img.width * cover;
    const dh = img.height * cover;
    const dx = (cw - dw) / 2;
    const dy = (ch - dh) / 2;

    ctx.fillStyle = this.dataset.background || '#000000';
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(img, dx, dy, dw, dh);
  }

  /* ---------- overlays ---------- */

  band(f, a, b, fadeIn, fadeOut) {
    const out = fadeOut === undefined ? fadeIn : fadeOut;
    if (f < a || f > b) return 0;
    if (f < a + fadeIn) return (f - a) / fadeIn;
    if (f > b - out) return (b - f) / out;
    return 1;
  }

  syncOverlays(f) {
    if (this.hero) {
      let o = 1;
      if (f > this.fadeStart) {
        const span = Math.max(0.001, this.fadeEnd - this.fadeStart);
        o = 1 - Math.max(0, Math.min(1, (f - this.fadeStart) / span));
      }
      this.hero.style.opacity = o.toFixed(3);
      this.hero.style.transform = `translateY(${((1 - o) * -40).toFixed(1)}px)`;
      this.hero.style.pointerEvents = o > 0.6 ? 'auto' : 'none';
    }

    if (this.brand) {
      this.brand.style.opacity = '1';
      this.brand.style.pointerEvents = 'none';
    }

    // The lockup holds its authored size for the whole sequence — only a short rise-in.
    if (this.lockup) {
      const rise = Math.max(0, Math.min(1, (f - this.brandIn) / Math.max(0.001, this.brandRise)));
      this.lockup.style.transform = `translateY(${((1 - rise) * 22).toFixed(1)}px)`;
    }

    this.captions.forEach((el) => {
      const start = parseFloat(el.dataset.start || '0') / 100;
      const end = parseFloat(el.dataset.end || '100') / 100;
      const o = this.band(f, start, end, Math.max(0.02, (end - start) * 0.28));
      el.style.opacity = o.toFixed(3);
      el.style.transform = `translateY(${((1 - o) * 28).toFixed(1)}px)`;
      el.style.pointerEvents = o > 0.6 ? 'auto' : 'none';
    });

    if (this.btnWrap) {
      const bStart = parseFloat(this.btnWrap.dataset.start || '0') / 100;
      const bEnd = parseFloat(this.btnWrap.dataset.end || '100') / 100;
      let bo = 1;
      if (f < bStart || f >= bEnd) {
        bo = 0;
      } else {
        const fadeRamp = 0.08;
        if (bStart > 0 && f < bStart + fadeRamp) {
          bo = (f - bStart) / fadeRamp;
        } else if (f > bEnd - fadeRamp) {
          bo = (bEnd - f) / fadeRamp;
        }
      }
      bo = Math.max(0, Math.min(1, bo));
      this.btnWrap.style.opacity = bo.toFixed(3);
      this.btnWrap.style.pointerEvents = bo > 0.3 ? 'auto' : 'none';
      this.btnWrap.style.visibility = bo <= 0 ? 'hidden' : 'visible';
    }

    // Update live frame counter
    if (this.frameNumEl) {
      const num = this.index + 1;
      this.frameNumEl.textContent = num < 10 ? '0' + num : String(num);
    }

    this.dispatchEvent(
      new CustomEvent('kinstone:frame', { detail: { fraction: f, index: this.index }, bubbles: true })
    );
  }

  /* ---------- interaction ---------- */

  bind() {
    this.onScroll = () => this.requestDraw();
    this.onResize = () => {
      this.sizeCanvas();
      this.draw();
    };
    this.onMouse = (e) => this.applyParallax(e);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    if (this.parallax > 0 && window.matchMedia('(hover: hover)').matches) {
      window.addEventListener('mousemove', this.onMouse);
    }

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => {
        this.sizeCanvas();
        this.draw();
      });
      document.addEventListener('shopify:section:select', () => {
        this.sizeCanvas();
        this.draw();
      });
    }

    if (this.topBtn) {
      this.topBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (window.gsap && window.ScrollToPlugin) {
          window.gsap.to(window, { duration: 1.2, scrollTo: 0, ease: 'power2.inOut' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    }
  }

  applyParallax(e) {
    if (!this.canvas) return;
    const x = (e.clientX / window.innerWidth - 0.5) * -2 * this.parallax;
    const y = (e.clientY / window.innerHeight - 0.5) * -2 * this.parallax;
    if (window.gsap) {
      window.gsap.to(this.canvas, { x, y, duration: 1.1, ease: 'power3.out' });
    } else {
      this.canvas.style.transform = `scale(1.05) translate(${x}px, ${y}px)`;
    }
  }
}

if (!customElements.get('kinstone-cinematic-hero')) {
  customElements.define('kinstone-cinematic-hero', KinstoneCinematicHero);
}
